import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import OkDocViewer from "@/components/OkDocViewer";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, Inbox, ClipboardCheck, RefreshCw, FileText, RotateCcw,
  CheckCircle2, XCircle, HelpCircle, Download, ChevronLeft, ChevronRight,
} from "lucide-react";
import { MobileSidebar, BurgerBar } from "@/components/MobileNav";

const TABS = [
  { k: "alle", label: "Alle" },
  { k: "offen", label: "Offen" },
  { k: "ok", label: "OK" },
  { k: "not_ok", label: "Not OK" },
  { k: "unklar", label: "Unklar" },
];
const matchTab = (d, tab) =>
  tab === "alle" ? true : tab === "offen" ? !d.qk_status : d.qk_status === tab;

const KIND_BADGE = {
  gesamtausgabe: { label: "Gesamt", cls: "bg-indigo-50 text-indigo-700 border-indigo-300" },
  inhaltsverzeichnis: { label: "Inhalt", cls: "bg-sky-50 text-sky-700 border-sky-300" },
  entry: { label: "Eintrag", cls: "bg-gray-100 text-gray-600 border-gray-300" },
};

export default function QK() {
  const location = useLocation();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("alle");
  const [landFilter, setLandFilter] = useState("alle");
  const [blattFilter, setBlattFilter] = useState("alle");
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const selectedRef = useRef(null);
  const openedTsRef = useRef(null);
  const toggleHeft = (h) => setCollapsed((c) => ({ ...c, [h]: !c[h] }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/ok-documents");
      setDocs(r.data);
    } catch { toast.error("OK-Dokumente konnten nicht geladen werden"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Aus „Hefte Heute" (Ampel-Klick) geöffnetes Heft → ersten Split-Eintrag auswählen + Gruppe aufklappen.
  useEffect(() => {
    const st = location.state;
    if (!st?.openParent || st.ts === openedTsRef.current || docs.length === 0) return;
    const target = docs.find((d) => d.parent_id === st.openParent);
    if (target) {
      openedTsRef.current = st.ts;
      setSelectedId(target.id);
      setCollapsed((c) => ({ ...c, [target.heft_filename]: false }));
    }
  }, [location.state, docs]);

  const matchFilters = useCallback((d) =>
    (landFilter === "alle" || d.land === landFilter)
    && (blattFilter === "alle" || d.blatt === blattFilter), [landFilter, blattFilter]);

  const filtered = useMemo(() => docs.filter((d) => matchTab(d, tab) && matchFilters(d)), [docs, tab, matchFilters]);

  const counts = useMemo(() => {
    const scope = docs.filter(matchFilters);
    return {
      alle: scope.length,
      offen: scope.filter((d) => !d.qk_status).length,
      ok: scope.filter((d) => d.qk_status === "ok").length,
      not_ok: scope.filter((d) => d.qk_status === "not_ok").length,
      unklar: scope.filter((d) => d.qk_status === "unklar").length,
    };
  }, [docs, matchFilters]);

  const landOptions = useMemo(() => Array.from(new Set(docs.map((d) => d.land).filter(Boolean))).sort(), [docs]);
  const blattOptions = useMemo(() => Array.from(new Set(
    docs.filter((d) => landFilter === "alle" || d.land === landFilter).map((d) => d.blatt).filter(Boolean))).sort(), [docs, landFilter]);

  useEffect(() => {
    if (blattFilter !== "alle" && !blattOptions.includes(blattFilter)) setBlattFilter("alle");
  }, [blattOptions, blattFilter]);

  // Nach Heft gruppieren (Reihenfolge beibehalten).
  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((d) => {
      if (!map.has(d.heft_filename)) map.set(d.heft_filename, []);
      map.get(d.heft_filename).push(d);
    });
    return Array.from(map.entries());
  }, [filtered]);

  useEffect(() => {
    if (selectedId && selectedRef.current) selectedRef.current.scrollIntoView({ block: "nearest" });
  }, [selectedId, filtered]);

  const selected = docs.find((d) => d.id === selectedId) || null;

  // Geschwister-Einträge desselben Hefts — für schnellen Wechsel ohne Burger-Menü.
  const heftSiblings = useMemo(() => {
    if (!selected) return [];
    return docs
      .filter((d) => d.heft_filename === selected.heft_filename)
      .sort((a, b) => (Number(a.vonseite) - Number(b.vonseite)) || String(a.vonzusatz || "").localeCompare(String(b.vonzusatz || "")));
  }, [docs, selected]);
  const siblingIdx = heftSiblings.findIndex((d) => d.id === selectedId);
  const gotoSibling = (i) => { if (i >= 0 && i < heftSiblings.length) setSelectedId(heftSiblings[i].id); };

  const setQk = async (id, val) => {
    setBusy(true);
    try {
      await api.put(`/ok-documents/${id}`, { qk_status: val });
      toast.success(`Status gesetzt: ${val === "not_ok" ? "Not OK" : val.toUpperCase()}`);
      setDocs((ds) => ds.map((d) => (d.id === id ? { ...d, qk_status: val } : d)));
    } catch { toast.error("Konnte Status nicht setzen"); }
    finally { setBusy(false); }
  };

  const deferHeft = async (parentId) => {
    setBusy(true);
    try {
      await api.put(`/documents/${parentId}`, { status: "zurueckgestellt", qk_status: "" });
      toast.success("Heft zurückgestellt", { description: "Es ist jetzt in der PP bearbeitbar; OK-Einträge wurden entfernt." });
      setSelectedId(null);
      load();
    } catch { toast.error("Zurückstellen fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const exportHeft = async (parentId) => {
    setBusy(true);
    try {
      await api.post(`/documents/${parentId}/export`);
      toast.success("Heft exportiert", { description: "Export-Zeit gesetzt (in Hefte Heute grün)." });
    } catch (e) {
      toast.error("Export nicht möglich", { description: e?.response?.data?.detail || "Export fehlgeschlagen" });
    } finally { setBusy(false); }
  };

  const okActions = selected ? (
    <>
      <Button size="sm" disabled={busy} onClick={() => setQk(selected.id, "ok")} data-testid="ok-set-ok" title="Eintrag als OK markieren"
        className={selected.qk_status === "ok" ? "bg-green-600 hover:bg-green-700 text-white" : "bg-white border border-green-500 text-green-700 hover:bg-green-50"}>
        <CheckCircle2 className="h-4 w-4 mr-1" /> OK
      </Button>
      <Button size="sm" disabled={busy} onClick={() => setQk(selected.id, "not_ok")} data-testid="ok-set-not-ok" title="Eintrag als Not OK markieren"
        className={selected.qk_status === "not_ok" ? "bg-red-600 hover:bg-red-700 text-white" : "bg-white border border-red-500 text-red-700 hover:bg-red-50"}>
        <XCircle className="h-4 w-4 mr-1" /> Not OK
      </Button>
      <Button size="sm" disabled={busy} onClick={() => setQk(selected.id, "unklar")} data-testid="ok-set-unklar" title="Eintrag als Unklar markieren"
        className={selected.qk_status === "unklar" ? "bg-amber-500 hover:bg-amber-600 text-white" : "bg-white border border-amber-400 text-amber-700 hover:bg-amber-50"}>
        <HelpCircle className="h-4 w-4 mr-1" /> Unklar
      </Button>
      <Button size="sm" variant="outline" disabled={busy} className="border-gray-400 text-gray-700 hover:bg-gray-100" onClick={() => deferHeft(selected.parent_id)} data-testid="ok-defer-heft" title="Heft zurückstellen (zurück in die PP)">
        <RotateCcw className="h-4 w-4 mr-1" /> Heft zurückstellen
      </Button>
      <Button size="sm" disabled={busy} className="bg-[#0F4C81] hover:bg-[#0d3f6c] text-white" onClick={() => exportHeft(selected.parent_id)} data-testid="ok-export-heft" title="Heft exportieren (Export-Zeit setzen)">
        <Download className="h-4 w-4 mr-1" /> Exportieren
      </Button>
    </>
  ) : null;

  const sidebar = (
    <>
      <div className="shrink-0 p-3 border-b border-gray-200 bg-white space-y-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="h-8 w-8 rounded-lg bg-amber-600 flex items-center justify-center shrink-0">
              <ClipboardCheck className="h-4 w-4 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="font-heading font-bold text-gray-900 text-sm">OK</h2>
              <p className="text-[11px] text-gray-400">{docs.length} Einträge · {groups.length} Hefte</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="qk-refresh" title="Liste aktualisieren"><RefreshCw className="h-4 w-4" /></Button>
        </div>
        <div className="flex flex-wrap gap-1">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`ok-tab-${t.k}`}
              className={`px-2 py-1 text-[11px] font-semibold rounded border transition-colors ${tab === t.k ? "bg-[#0F4C81] text-white border-[#0F4C81]" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}>
              {t.label} <span className={tab === t.k ? "text-white/80" : "text-gray-400"}>{counts[t.k]}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Select value={landFilter} onValueChange={(v) => { setLandFilter(v); setBlattFilter("alle"); }}>
            <SelectTrigger className="h-8 text-xs" data-testid="ok-filter-land"><SelectValue placeholder="Bundesland" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="alle" data-testid="ok-filter-land-alle">Alle Bundesländer</SelectItem>
              {landOptions.map((l) => (
                <SelectItem key={l} value={l} data-testid={`ok-filter-land-${l}`}>{l.toUpperCase()}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={blattFilter} onValueChange={setBlattFilter}>
            <SelectTrigger className="h-8 text-xs" data-testid="ok-filter-blatt"><SelectValue placeholder="Heft-Art" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="alle" data-testid="ok-filter-blatt-alle">Alle Heft-Arten</SelectItem>
              {blattOptions.map((b) => (
                <SelectItem key={b} value={b} data-testid={`ok-filter-blatt-${b}`}>{b.toUpperCase()}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="py-16 flex justify-center text-gray-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : groups.length === 0 ? (
          <div className="py-16 text-center text-gray-400">
            <Inbox className="h-7 w-7 mx-auto" />
            <p className="mt-2 text-sm">Keine Einträge in dieser Ansicht.</p>
          </div>
        ) : (
          groups.map(([heft, items]) => (
            <div key={heft} className="mb-1 border-t-4 border-gray-200 first:border-t-0">
              <button type="button" onClick={() => toggleHeft(heft)} data-testid={`ok-heft-toggle-${heft}`}
                title={collapsed[heft] ? "Heft aufklappen" : "Heft zuklappen"}
                className="w-full flex items-center gap-2 px-4 pt-3 pb-2 bg-gray-100/70 hover:bg-gray-200/70 transition-colors text-left">
                <ChevronRight className={`h-4 w-4 text-gray-600 shrink-0 transition-transform duration-200 ${collapsed[heft] ? "" : "rotate-90"}`} />
                <span className="flex-1 min-w-0 text-base font-bold text-gray-900 font-mono truncate" data-testid="ok-heft-header">{heft.replace(/\.pdf$/i, "")}</span>
                <span className="shrink-0 text-xs font-semibold text-gray-500 tabular-nums bg-white border border-gray-300 rounded px-1.5 py-0.5">{items.length}</span>
              </button>
              {!collapsed[heft] && items.map((d) => {
                const active = d.id === selectedId;
                const kb = KIND_BADGE[d.kind] || KIND_BADGE.entry;
                const isGesamt = d.kind === "gesamtausgabe";
                return (
                  <button key={d.id} ref={active ? selectedRef : null} onClick={() => { setSelectedId(d.id); setNavOpen(false); }} data-testid={`ok-doc-${d.id}`}
                    className={`w-full text-left px-4 py-2.5 border-b border-gray-200 transition-colors ${active ? "bg-[#0F4C81]/10 border-l-4 border-l-[#0F4C81]" : isGesamt ? "bg-indigo-50 border-l-4 border-l-indigo-400 hover:bg-indigo-100/70" : "border-l-4 border-l-transparent hover:bg-white"}`}>
                    <div className="flex items-center gap-2">
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border shrink-0 ${kb.cls}`}>{kb.label}</span>
                      <span className={`flex-1 min-w-0 truncate text-base font-medium ${active ? "text-[#0F4C81]" : "text-gray-800"}`}>{d.drstitel || "(ohne Titel)"}</span>
                    </div>
                    <div className="mt-1.5 flex items-center justify-between gap-2">
                      <span className="text-base text-gray-900">
                        S.&nbsp;{d.vonseite}–{d.bisseite}{d.vonzusatz ? <> · Pos.&nbsp;{d.vonzusatz}</> : null}
                      </span>
                      <StatusBadge status={d.qk_status || "offen"} testId={`ok-doc-status-${d.id}`} />
                    </div>
                  </button>
                );
              })}
            </div>
          ))
        )}
      </div>
    </>
  );

  return (
    <div className="h-full flex">
      {/* LEFT: split entries grouped by Heft — desktop inline */}
      <div className="hidden xl:flex w-96 shrink-0 border-r border-gray-200 bg-gray-50 flex-col min-h-0" data-testid="qk-list">
        {sidebar}
      </div>
      {/* LEFT: mobile burger sheet */}
      <MobileSidebar open={navOpen} onOpenChange={setNavOpen} title="OK-Einträge">
        <div className="flex flex-col min-h-0 h-full bg-gray-50">{sidebar}</div>
      </MobileSidebar>

      {/* RIGHT: split PDF + XML + QK actions */}
      <div className="flex-1 min-w-0 h-full bg-white flex flex-col min-h-0">
        <BurgerBar onOpen={() => setNavOpen(true)} label={selected ? (selected.drstitel || "(ohne Titel)") : "Eintrag wählen"} />

        {/* Schneller Eintrags-Wechsler innerhalb des Hefts */}
        {selected && heftSiblings.length > 1 && (
          <div className="shrink-0 flex items-center gap-1.5 px-2 py-1.5 border-b border-gray-200 bg-gray-50" data-testid="ok-entry-switcher">
            <Button size="sm" variant="outline" className="h-8 w-8 p-0 shrink-0" disabled={siblingIdx <= 0}
              onClick={() => gotoSibling(siblingIdx - 1)} data-testid="ok-entry-prev" title="Vorheriger Eintrag">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex-1 min-w-0 overflow-x-auto flex items-center gap-1 no-scrollbar">
              {heftSiblings.map((s, i) => {
                const active = s.id === selectedId;
                const kb = KIND_BADGE[s.kind] || KIND_BADGE.entry;
                return (
                  <button key={s.id} onClick={() => setSelectedId(s.id)} data-testid={`ok-entry-chip-${s.id}`}
                    title={s.drstitel || "(ohne Titel)"}
                    className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md border text-[11px] font-medium transition-colors ${active ? "bg-[#0F4C81] text-white border-[#0F4C81]" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"}`}>
                    <span className={`text-[8px] font-bold px-1 py-0.5 rounded border ${active ? "bg-white/20 text-white border-white/30" : kb.cls}`}>{kb.label}</span>
                    <span className="tabular-nums">S.&nbsp;{s.vonseite}{s.vonzusatz ? `·${s.vonzusatz}` : ""}</span>
                  </button>
                );
              })}
            </div>
            <span className="shrink-0 text-[11px] text-gray-400 tabular-nums px-1" data-testid="ok-entry-counter">{siblingIdx + 1}/{heftSiblings.length}</span>
            <Button size="sm" variant="outline" className="h-8 w-8 p-0 shrink-0" disabled={siblingIdx >= heftSiblings.length - 1}
              onClick={() => gotoSibling(siblingIdx + 1)} data-testid="ok-entry-next" title="Nächster Eintrag">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}

        <div className="flex-1 min-h-0">
          {selectedId ? (
            <OkDocViewer okId={selectedId} actions={okActions} />
          ) : (
            <div className="h-full flex items-center justify-center text-center p-8">
              <div className="text-gray-300">
                <FileText className="h-12 w-12 mx-auto" />
                <p className="mt-3 text-sm text-gray-500">Wählen Sie einen Eintrag zur Durchsicht (PDF + XML).</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
